// ============================================================================
// AFB-0075 / PR 3 — the packet tables, read and written.
//
// A PACKET is an ordered set of templates plus the rules for who gets which of
// them: "W2 onboarding" is eight documents, three of which are state
// withholding forms that are mutually exclusive. The rules live on the join
// row (esign_packet_documents.applies_to / applies_to_states), never in code,
// because a new state form is a data change and must not be a deploy.
//
// This is the network half only. Everything that DECIDES anything is pure and
// lives in ./packet-composition, which a node test can import — envelope-lib.ts
// cannot be imported by one at all, and the page-offset arithmetic is precisely
// the code that must be tested. Re-exported at the bottom so a component has
// one import site, the same arrangement envelope-lib.ts uses.
//
// packet_id (PKT-NNNN) and link_id (PKD-NNNN) are assigned by BEFORE INSERT
// triggers and are NEVER sent from here. An insert that supplies one either
// collides with the sequence or silently wins against it.
// ============================================================================

import { supabase } from '../../shared/supabase';
import { esignInternal } from './lib';
import {
  PacketDocumentRow,
  RawPacketDocumentRow,
  normalisePacketDocumentRow,
} from './packet-composition';
import type { PacketPlan } from './packet-launch';

/** One row of esign_packets. */
export interface PacketRow {
  packet_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_by_eid: string | null;
  /** Covering email + signing note, copied onto the envelope at send time. */
  email_subject: string | null;
  email_body: string | null;
  note_to_signer: string | null;
  created_at: string;
  updated_at: string;
}

/** The fields a person edits on a packet. Everything else is bookkeeping. */
export interface PacketMeta {
  name: string;
  description: string | null;
  email_subject: string | null;
  email_body: string | null;
  note_to_signer: string | null;
  is_active: boolean;
}

/** The per-document rules a person edits on one row of the packet. */
export interface PacketDocumentPatch {
  applies_to: string[] | null;
  applies_to_states: string[] | null;
  is_required: boolean;
}

/**
 * Every packet, active first then by name.
 *
 * Inactive packets are INCLUDED — the editor is where is_active is turned back
 * on, so a list that hid them would make deactivating one irreversible from the
 * only screen that can do it.
 */
export async function listPackets(): Promise<PacketRow[]> {
  const { data, error } = await supabase
    .from('esign_packets')
    .select('*')
    .order('is_active', { ascending: false })
    .order('name');
  if (error) throw error;
  return (data ?? []) as PacketRow[];
}

/**
 * Create an empty packet. Only the name is required; everything else is edited
 * afterwards on the row itself, so the create step cannot become a form.
 */
export async function createPacket(input: {
  name: string;
  created_by_eid: string | null;
}): Promise<PacketRow> {
  const { data, error } = await supabase
    .from('esign_packets')
    .insert({ name: input.name, created_by_eid: input.created_by_eid } as never)
    .select()
    .single();
  if (error) throw error;
  return data as PacketRow;
}

/**
 * Save any subset of the editable fields.
 *
 * Blank text is written as NULL rather than '' so "no covering subject" has one
 * representation — the send path tests these for null, and an empty string
 * would pass that test and then send an empty subject line.
 */
export async function savePacketMeta(
  packet_id: string,
  meta: Partial<PacketMeta>,
): Promise<void> {
  const patch: Partial<PacketMeta> = { ...meta };
  for (const key of ['description', 'email_subject', 'email_body', 'note_to_signer'] as const) {
    const value = patch[key];
    if (typeof value === 'string') patch[key] = value.trim() === '' ? null : value;
  }
  if (typeof patch.name === 'string') patch.name = patch.name.trim();
  const { error } = await supabase
    .from('esign_packets')
    .update(patch as never)
    .eq('packet_id', packet_id);
  if (error) throw error;
}

/** The documents on one packet, in sort_order, with jsonb made trustworthy. */
export async function listPacketDocuments(packet_id: string): Promise<PacketDocumentRow[]> {
  const { data, error } = await supabase
    .from('esign_packet_documents')
    .select('*')
    .eq('packet_id', packet_id)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as RawPacketDocumentRow[]).map(normalisePacketDocumentRow);
}

/**
 * Every packet's documents in one read, for the launcher.
 *
 * The picker has to answer "does any active packet apply to this person?"
 * before it can decide whether the button is even pressable, and one query
 * beats one-per-packet for a table that holds a few dozen rows in total.
 */
export async function listAllPacketDocuments(): Promise<PacketDocumentRow[]> {
  const { data, error } = await supabase
    .from('esign_packet_documents')
    .select('*')
    .order('packet_id', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as RawPacketDocumentRow[]).map(normalisePacketDocumentRow);
}

// ── the two packet actions on the esign Edge Function ───────────────────────

/** The per-send document override. NULL on a half means "no override". */
export interface PacketPlanArgs {
  packet_id: string;
  onboarding_id: string;
  include?: string[] | null;
  exclude?: string[] | null;
}

/**
 * What this packet would send this person: documents in order with their page
 * offsets, the resolved merge values, and every gap.
 *
 * READ-ONLY — esign_packet_plan writes nothing at all, so a review screen may
 * call it as often as it likes, including on every tick of the document list.
 */
export function packetPlan(args: PacketPlanArgs): Promise<PacketPlan> {
  return esignInternal<PacketPlan>({
    action: 'packet_plan',
    packet_id: args.packet_id,
    onboarding_id: args.onboarding_id,
    include: args.include ?? undefined,
    exclude: args.exclude ?? undefined,
  });
}

/** What env_from_packet returns once the envelope exists. */
export interface PacketEnvelopeResult {
  ok: boolean;
  envelope_id: string;
  total_pages: number;
  documents: Array<{ template_id: string; name: string; page_count: number }>;
  /** Things the builder could not resolve. Never a reason to auto-send anyway. */
  unresolved: Array<{ kind: string; reason: string }>;
}

/**
 * Assemble the packet and create the envelope — as a DRAFT, always.
 *
 * Nothing here sends anything. The envelope lands on the review screen and a
 * separate, deliberate click sends it; a blocked packet (409 packet_blocked)
 * creates nothing at all.
 */
export function envFromPacket(args: PacketPlanArgs): Promise<PacketEnvelopeResult> {
  return esignInternal<PacketEnvelopeResult>({
    action: 'env_from_packet',
    packet_id: args.packet_id,
    onboarding_id: args.onboarding_id,
    include: args.include ?? undefined,
    exclude: args.exclude ?? undefined,
  });
}

/**
 * Put a template on the end of a packet.
 *
 * The caller supplies sort_order because it knows the current list; a
 * server-side max()+1 would need a round trip and would still race. The UNIQUE
 * on (packet_id, template_id) is what stops the same document being added
 * twice, and the error it raises is reported rather than pre-empted — a check
 * done in the browser is a check that is wrong the moment somebody else saves.
 */
export async function addPacketDocument(input: {
  packet_id: string;
  template_id: string;
  sort_order: number;
}): Promise<void> {
  const { error } = await supabase
    .from('esign_packet_documents')
    .insert({
      packet_id: input.packet_id,
      template_id: input.template_id,
      sort_order: input.sort_order,
    } as never);
  if (error) throw error;
}

/**
 * Save the rules on one document row.
 *
 * An empty list is written as NULL, matching normaliseRestrictionList on the
 * way in: NULL and [] both mean "no restriction", and storing both leaves two
 * spellings of one fact for the next reader to get wrong.
 */
export async function savePacketDocument(
  link_id: string,
  patch: PacketDocumentPatch,
): Promise<void> {
  const { error } = await supabase
    .from('esign_packet_documents')
    .update({
      applies_to: patch.applies_to && patch.applies_to.length > 0 ? patch.applies_to : null,
      applies_to_states:
        patch.applies_to_states && patch.applies_to_states.length > 0
          ? patch.applies_to_states
          : null,
      is_required: patch.is_required,
    } as never)
    .eq('link_id', link_id);
  if (error) throw error;
}

/**
 * Take a document off a packet. A REAL delete — the table has a DELETE policy
 * and no is_active column, so there is nothing to soft-delete to. Envelopes
 * already sent from this packet are unaffected; they hold their own copies.
 */
export async function removePacketDocument(link_id: string): Promise<void> {
  const { error } = await supabase
    .from('esign_packet_documents')
    .delete()
    .eq('link_id', link_id);
  if (error) throw error;
}

/**
 * Write the given order back as sort_order 1..n.
 *
 * One update per row rather than an upsert: an upsert would have to send
 * link_id, and every other column with it, which is how a reorder quietly
 * reverts somebody else's applies_to edit. Packets hold a handful of documents,
 * so the round trips are cheap. Sequential rather than parallel so a failure
 * halfway leaves a PREFIX renumbered rather than an arbitrary scatter.
 */
export async function reorderPacketDocuments(ordered: PacketDocumentRow[]): Promise<void> {
  for (let i = 0; i < ordered.length; i += 1) {
    const row = ordered[i];
    if (row.sort_order === i + 1) continue;
    const { error } = await supabase
      .from('esign_packet_documents')
      .update({ sort_order: i + 1 } as never)
      .eq('link_id', row.link_id);
    if (error) throw error;
  }
}

// The rules are pure and live in ./packet-composition, which is testable.
// Re-exported so a component has one import site — same arrangement as
// envelope-lib.ts and ./template-bindings.
export {
  WORKER_CLASSES,
  composePacket,
  describePageSpan,
  documentApplies,
  matchesState,
  matchesWorkerClass,
  moveDocument,
  normalisePacketDocumentRow,
  normaliseRestrictionList,
  selectPacketDocuments,
} from './packet-composition';
export type {
  ComposedPacketDocument,
  PacketAudience,
  PacketComposition,
  PacketDocumentRow,
  PacketDocumentTemplate,
  PacketStateGap,
  RawPacketDocumentRow,
  WorkerClass,
} from './packet-composition';

// Same arrangement for the launch rules (AFB-0075 / PR 5): pure and tested in
// ./packet-launch, re-exported here so a component has one import site.
export {
  canLaunchPacket,
  canSendPacket,
  computeDocumentOverride,
  defaultPacketFor,
  isMergeFieldResolved,
  launchBlockers,
  mergeFieldsFromPlan,
  packetSendBlockers,
  packetServesWorkerClass,
  packetsForWorkerClass,
  parseDocumentOverrides,
  planBindingGapSentences,
  planGapSentences,
  planSelectedTemplateIds,
  seedSignerValues,
  unresolvedRequiredMergeFields,
} from './packet-launch';
export type {
  DocumentOverride,
  EnvelopeFieldLike,
  LaunchDocument,
  LaunchPacketOption,
  LaunchSubject,
  MergeFieldReview,
  PacketPlan,
  PlanDocument,
  PlanLayoutField,
  PlanRole,
  SeedableField,
  SendGateInput,
} from './packet-launch';
